import api from '@/plugins/kitsu';

import requireAuth from '@/services/interceptors/auth';
import requireOperatingCompany from '@/services/interceptors/operatingCompany';
// import Qs from 'qs';

const http = api('planning_service/admin', {
  middleware: [requireAuth, requireOperatingCompany],
});

export default {
  // Planned Jobs
  getAdminPlannedJobs: (params = {}) =>
    http.get('planned-jobs', {
      include:
        'job,project.manager,currentCreatedPhase.phase,currentCreatedPhase.assignedUsers,account,userAssignments,machineAssignments,workInstruction,assignments.assignable,assignments.userFunction,workOrders,stockOrders',
      page: {
        limit: 100,
      },
      ...params,
    }),
  getAdminPlannedJob: (id, params = {}) =>
    http.get(`planned-jobs/${id}`, {
      include:
        'job,project.manager,project.contact,currentCreatedPhase.phase,currentCreatedPhase.assignedUsers,account,workInstruction,assignments.assignable,assignments.userFunction,workOrders,stockOrders,userAssignments.userChildren.assignable,userAssignments.machineChildren.assignable,machineAssignments.assignable',
      ...params,
    }),
  postAdminPlannedJob: formData => http.post('planned-jobs', formData),
  patchAdminPlannedJob: (id, formData) =>
    http.patch('planned-jobs', { id, ...formData }),
  getAdminPlannedJobTemplate: (params = {}) =>
    http.get(`planned_job_templates`, {
      ...params,
    }),
  deleteAdminPlannedJob: id => http.delete('planned-jobs', id),
  publishAdminPlannedJobs: data =>
    http.post('planned-jobs/publish-range', data),

  // Internal employees
  getAdminInternalEmployee: (id, params = {}) =>
    http.get(`internal-employees/${id}`, {
      include: 'userFunctions',
      ...params,
    }),
  getAdminInternalEmployees: (params = {}) =>
    http.get('internal-employees', {
      include: 'userFunctions',
      ...params,
    }),

  // External employees
  getAdminExternalEmployees: (params = {}) =>
    http.get('external-employees', {
      include: 'userFunctions,employmentAgency',
      ...params,
    }),

  // Employment agencies
  getAdminEmploymentAgencies: (params = {}) =>
    http.get('employment-agencies', {
      include: 'availableExternalEmployees.userFunctions',
      ...params,
    }),
  getAdminEmploymentAgency: (id, params = {}) =>
    http.get(`employment-agencies/${id}`, {
      ...params,
    }),

  // Machines
  getAdminMachines: (params = {}) =>
    http.get('machines', {
      ...params,
    }),

  // Items
  getAdminItems: (params = {}) =>
    http.get('items', {
      include: 'machines',
      ...params,
    }),
  getAdminItem: (id, params = {}) =>
    http.get(`items/${id}`, {
      include: 'machines',
      ...params,
    }),

  // Projects
  getAdminProjects: (params = {}) =>
    http.get('projects', {
      include: 'account',
      ...params,
    }),
  getAdminProject: (id, params = {}) =>
    http.get(`projects/${id}`, {
      include: 'account',
      ...params,
    }),

  // Jobs
  getAdminJobs: (params = {}) =>
    http.get('jobs', {
      include: 'project.account',
      page: {
        limit: 100,
      },
      ...params,
    }),
  getAdminJob: (id, params = {}) =>
    http.get(`jobs/${id}`, {
      include: 'project.account',
      ...params,
    }),

  // Accounts
  getAdminAccounts: (params = {}) =>
    http.get('accounts', {
      ...params,
    }),
  getAdminAccount: (id, params = {}) =>
    http.get(`accounts/${id}`, {
      ...params,
    }),

  // Leave requests
  getAdminLeaveRequests: (params = {}) =>
    http.get('leave-requests', {
      include: 'user',
      ...params,
    }),

  // Sick registrations
  getAdminSickRegistrations: (params = {}) =>
    http.get('sick-registrations', {
      include: 'user',
      ...params,
    }),

  // Reparations
  getAdminReparations: (params = {}) =>
    http.get('reparations', {
      ...params,
    }),

  // Rental lines
  getAdminRentalLines: (params = {}) =>
    http.get('rental-lines', {
      include: 'machine,account',
      ...params,
    }),

  // Planning assignments
  getAdminPlanningAssignment: (id, params = {}) =>
    http.get(`planning-assignments/${id}`, {
      include:
        'assignable,plannedJob,job,project,userFunction,userChildren.assignable,userChildren.userFunction,machineChildren.assignable,parentAssignment.assignable,parentAssignment.userFunction',
      ...params,
    }),
  getAdminPlanningAssignments: (params = {}) =>
    http.get('planning-assignments', {
      include: 'plannedJob,job,project,plannedJob.account,userFunction',
      fields: {
        plannedJobs: 'resourcesPlanned,published',
        jobs: 'name,jobType,fullCode',
        projects: 'description,code',
        accounts: 'name',
        userFunctions: 'name',
      },
      page: {
        limit: 100,
      },
      ...params,
    }),
  postAdminPlanningAssignment: formData =>
    http.post('planning-assignments', formData),
  patchAdminPlanningAssignment: (id, formData) =>
    http.patch('planning-assignments', { id, ...formData }),
  deleteAdminPlanningAssignment: (id, updateType, updateDate) =>
    http.delete('planning-assignments', id, {
      updateType: updateType,
      updateDate: updateDate,
    }),

  // Groups
  getAdminGroups: (params = {}) =>
    http.get('groups', {
      ...params,
    }),
  getAdminGroup: (id, params = {}) =>
    http.get(`groups/${id}`, {
      ...params,
    }),

  // Planning blocks
  getAdminPlanningWorkShiftBlocks: (params = {}) =>
    http.get('planning-work-shift-blocks', {
      ...params,
    }),
  getAdminPlanningJobScheduleBlocks: (params = {}) =>
    http.get('planning-job-schedule-blocks', {
      ...params,
    }),

  // User functions
  getAdminUserFunctions: (params = {}) =>
    http.get('user-functions', {
      ...params,
    }),
  getAdminUserFunction: (id, params = {}) =>
    http.get(`user-functions/${id}`, {
      ...params,
    }),

  // User wok shifts
  getAdminUserWorkShifts: (params = {}) =>
    http.get('user-work-shifts', {
      ...params,
    }),
  getAdminUserWorkShift: (userId, params = {}) =>
    http.get(`user-work-shifts/user/${userId}`, {
      ...params,
    }),

  // Machine schedules
  getAdminMachineSchedules: (params = {}) =>
    http.get('machine-schedules', {
      ...params,
    }),
  getAdminMachineSchedule: (machineId, params = {}) =>
    http.get(`machine-schedules/machine/${machineId}`, {
      ...params,
    }),

  // Missing certificate types
  getAdminMissingCertificateTypes: (params = {}) =>
    http.get('missing-certificate-types', {
      ...params,
    }),

  // Planning PDF
  getPlanningPdf: (params = {}) =>
    http.get(`planned-jobs.pdf`, {
      export_name: 'planning',
      ...params,
    }),
  // Preparation lists
  getPreparationList: (plannedJobId, params = {}) =>
    http.get(`planned-jobs/${plannedJobId}.pdf`, {
      export_name: 'preparation_list',
      ...params,
    }),
  getPreparationLists: (params = {}) =>
    http.get(`planned-jobs.pdf`, {
      export_name: 'preparation_list',
      ...params,
    }),

  // Cost centers
  getAdminCostCenters: (params = {}) =>
    http.get('cost-centers', {
      ...params,
    }),
  getAdminCostCenter: (id, params = {}) =>
    http.get(`cost-centers/${id}`, {
      ...params,
    }),

  // Users
  getAdminUsers: (params = {}) =>
    http.get('users', {
      ...params,
    }),
  getAdminUser: (id, params = {}) =>
    http.get(`users/${id}`, {
      ...params,
    }),
};
